import { useMemo, useState, type FormEvent } from 'react';
import Box from '@mui/material/Box';
import Seo from '@/components/Seo/Seo';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CustomButton from '@/components/Button/button';
import AuthPageLayout from '@/components/Layout/AuthPageLayout/authPageLayout';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
import {
  RateLimitError,
  resendVerificationByEmail,
  resendVerificationByToken,
} from '@/services/api/authService';
import CustomTextField from '@/components/CustomTextField/textField';

type Verified = 'true' | 'false' | null;

interface QueryState {
  verified: Verified;
  token: string | null;
}

function readQuery(): QueryState {
  if (typeof window === 'undefined') {
    return { verified: null, token: null };
  }
  const params = new URLSearchParams(window.location.search);
  const verifiedRaw = params.get('verified');
  const verified: Verified =
    verifiedRaw === 'true' || verifiedRaw === 'false' ? verifiedRaw : null;
  return { verified, token: params.get('token') };
}

const GENERIC_ERROR = 'Something went wrong, please try again.';

function ErrorText({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Typography
      sx={{
        ...typography.bodySmall,
        display: 'block',
        color: color.merlot.default,
        textAlign: 'center',
        mt: '12px',
      }}
    >
      {children}
    </Typography>
  );
}

interface EmailFallbackFormProps {
  onSuccess: (description: string) => void;
}

function EmailFallbackForm({ onSuccess }: EmailFallbackFormProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    if (submitting || !email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await resendVerificationByEmail(email.trim());
      onSuccess(
        "If an account exists for that email, we've sent a fresh verification link. Please check your inbox."
      );
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof RateLimitError ? e.message : GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return <Box />;
  }

  return (
    <Box>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          justifyContent: 'center',
          maxWidth: '520px',
          mx: 'auto',
        }}
      >
        <CustomTextField
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          errorText={error ?? undefined}
          fullWidth
        />
        <CustomButton
          type="submit"
          variant="contained"
          disabled={submitting || !email.trim()}
          sx={{ width: '100%', marginBottom: 30 }}
        >
          {submitting ? (
            <CircularProgress size={18} sx={{ color: color.cream['100'] }} />
          ) : (
            'Resend to my email address'
          )}
        </CustomButton>
        <Typography
          sx={{
            ...typography.caption,
            display: 'block',
            color: color.charcoal['400'],
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          We&rsquo;ll send a new verification link if there&rsquo;s an
          unverified account with this email address. If nothing arrives,
          double-check the spelling.
        </Typography>
      </Box>
    </Box>
  );
}

interface FailureWithTokenProps {
  token: string;
  onDescriptionChange: (description: string) => void;
  onShowEmailForm: () => void;
}

function FailureWithToken({
  token,
  onDescriptionChange,
  onShowEmailForm,
}: FailureWithTokenProps): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleResend(): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await resendVerificationByToken(token);
      if (response.status === 'sent') {
        onDescriptionChange(
          "We've sent you a fresh verification link. Click it within the next few minutes to activate your account."
        );
        setDone(true);
      } else if (response.status === 'already_verified') {
        onDescriptionChange(
          'Open the app to log in and start exploring UK vineyards.'
        );
        setDone(true);
      } else if (response.status === 'unknown_token') {
        onShowEmailForm();
      }
    } catch (e) {
      setError(e instanceof RateLimitError ? e.message : GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return <Box />;
  }

  return (
    <Box>
      <Box
        sx={{
          mt: '24px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CustomButton
          variant="contained"
          onClick={handleResend}
          disabled={submitting}
          sx={{ minWidth: '260px' }}
        >
          {submitting ? (
            <CircularProgress size={18} sx={{ color: color.cream['100'] }} />
          ) : (
            'Resend verification email'
          )}
        </CustomButton>
      </Box>
      {error && <ErrorText>{error}</ErrorText>}
    </Box>
  );
}

export default function VerifyEmailPage(): JSX.Element {
  const { verified, token } = useMemo(() => readQuery(), []);

  const initialHeading =
    verified === 'true'
      ? 'Email verified'
      : verified === 'false'
        ? "Verification link didn't work"
        : 'Verify your email';

  const initialDescription =
    verified === 'true'
      ? 'Your email is confirmed. You can now log in via the app to start exploring UK vineyards.'
      : verified === 'false' && token
        ? 'This link may have expired or already been used. We can send you a fresh one.'
        : verified === 'false'
          ? 'The link is missing or invalid. Enter the email you signed up with and we will send a new verification link.'
          : 'Click the link in the email we sent you, or enter your address below and we will resend it.';

  const [description, setDescription] = useState(initialDescription);
  const [showEmailForm, setShowEmailForm] = useState(false);

  let content: JSX.Element;

  if (verified === 'true') {
    content = <Box />;
  } else if (verified === 'false' && token && !showEmailForm) {
    content = (
      <FailureWithToken
        token={token}
        onDescriptionChange={setDescription}
        onShowEmailForm={() => {
          setDescription(
            'The link is missing or invalid. Enter the email you signed up with and we will send a new verification link.'
          );
          setShowEmailForm(true);
        }}
      />
    );
  } else {
    content = <EmailFallbackForm onSuccess={setDescription} />;
  }

  return (
    <>
      <Seo
        title="Verify Your Email – UK Vineyard Guide"
        description="Confirm your email address to activate your UK Vineyard Guide account and start exploring English and Welsh wine country."
        canonicalUrl="https://www.ukvineyardguide.co.uk/verify-email"
      />
      <AuthPageLayout heading={initialHeading} description={description}>
        {content}
      </AuthPageLayout>
    </>
  );
}
